import { Link, useParams, useSearchParams } from "react-router-dom";
import useSWR from "swr";
import { Seo } from "@/components/Seo";
import { CtaBand } from "@/components/CtaBand";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { PageHeader } from "@/pages/ContentPage";
import { fetcher, swrOpts } from "@/lib/api";
import { abs, breadcrumbLd, LOGO } from "@/lib/seo";
import NotFound from "@/pages/NotFound";

const date = (s) => new Date(s).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

export const BlogCard = ({ p }) => (
  <article className="card-surface group overflow-hidden" data-testid={`blog-card-${p.slug}`}>
    <Link to={`/blog/${p.slug}/`} className="block">
      {p.featured_image && <div className="aspect-[16/9] overflow-hidden bg-raised"><img src={p.featured_image} alt={p.image_alt || ""} width="600" height="338" loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" /></div>}
      <div className="p-6">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gold">{p.category}</p>
        <h3 className="mt-2 font-display text-xl font-semibold leading-snug text-white group-hover:text-gold">{p.title}</h3>
        <p className="mt-2 line-clamp-3 text-sm text-gray-400">{p.excerpt}</p>
        <p className="mt-4 text-xs text-gray-500"><time dateTime={p.published_at}>{date(p.published_at)}</time></p>
      </div>
    </Link>
  </article>
);

export default function Blog() {
  const [sp] = useSearchParams();
  const cat = sp.get("category");
  const { data } = useSWR(`/blog${cat ? `?category=${cat}` : ""}`, fetcher, swrOpts);
  const crumbs = [{ name: "Home", path: "/" }, { name: "Blog", path: "/blog/" }];
  return (
    <>
      <Seo title="Cannabis Guides & DC Delivery Articles | SHADC Dispensary Blog"
        description="Guides from SHADC Dispensary: how to order cannabis delivery in Washington, DC, product education, edible basics and responsible-use tips."
        path="/blog/" noindex={Boolean(cat)} jsonLd={[breadcrumbLd(crumbs)]} />
      <PageHeader crumbs={crumbs} eyebrow="Learn" h1="Guides & Articles" intro="Cannabis education, product guides and delivery tips for adults 21+ in Washington, DC." />
      <div className="container-x py-12">
        <ul className="mb-8 flex flex-wrap gap-2" aria-label="Blog categories">
          <li><Link to="/blog/" className={`chip ${!cat ? "border-gold text-gold" : ""}`} data-testid="blog-filter-all">All</Link></li>
          {(data?.categories || []).map((c) => <li key={c.slug}><Link to={`/blog/?category=${c.slug}`} className={`chip ${cat === c.slug ? "border-gold text-gold" : ""}`} data-testid={`blog-filter-${c.slug}`}>{c.name}</Link></li>)}
        </ul>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="blog-grid">{(data?.items || []).map((p) => <BlogCard key={p.slug} p={p} />)}</div>
      </div>
      <CtaBand />
    </>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const { data: p, error } = useSWR(`/blog/${slug}`, fetcher, swrOpts);
  if (error?.response?.status === 404) return <NotFound />;
  if (!p) return <div className="container-x h-96 animate-pulse py-16" aria-busy="true" />;
  const path = `/blog/${p.slug}/`;
  const crumbs = [{ name: "Home", path: "/" }, { name: "Blog", path: "/blog/" }, { name: p.title, path }];
  const ld = { "@context": "https://schema.org", "@type": "BlogPosting", headline: p.title, description: p.meta_description, image: p.featured_image,
    datePublished: p.published_at, dateModified: p.updated_at, author: { "@type": "Organization", name: p.author },
    publisher: { "@type": "Organization", name: "SHADC Dispensary", logo: { "@type": "ImageObject", url: abs(LOGO) } }, mainEntityOfPage: abs(path) };
  return (
    <>
      <Seo title={p.seo_title || `${p.title} | SHADC`} description={p.meta_description} path={path} image={p.featured_image} type="article" jsonLd={[ld, breadcrumbLd(crumbs)]} />
      <article className="container-x max-w-3xl py-10 sm:py-14">
        <Breadcrumbs items={crumbs} />
        <p className="eyebrow mt-8">{p.category}</p>
        <h1 className="mt-3 text-4xl font-bold leading-tight text-white sm:text-5xl" data-testid="blog-post-title">{p.title}</h1>
        <p className="mt-5 text-sm text-gray-400">By {p.author} · Published <time dateTime={p.published_at}>{date(p.published_at)}</time>{p.updated_at !== p.published_at && <> · Updated <time dateTime={p.updated_at}>{date(p.updated_at)}</time></>}</p>
        {p.featured_image && <img src={p.featured_image} alt={p.image_alt || ""} width="1200" height="675" className="mt-8 aspect-[16/9] w-full rounded-3xl object-cover" />}
        <div className="rich mt-10 text-base sm:text-lg" dangerouslySetInnerHTML={{ __html: p.content }} data-testid="blog-post-content" />
        {p.location && (
          <div className="card-surface mt-10 flex flex-col gap-4 border-gold/40 p-6 sm:flex-row sm:items-center sm:justify-between" data-testid="blog-post-location-cta">
            <p className="text-white"><span className="block text-xs font-bold uppercase tracking-[0.18em] text-gold">Delivering to {p.location.name}</span>Delivery details, FAQs and nearby areas.</p>
            <div className="flex flex-wrap gap-3">
              <Link to={`/locations/${p.location.slug}/`} className="btn-ghost" data-testid="blog-post-location-link">{p.location.name} delivery</Link>
              <Link to="/shop/" className="btn-gold" data-testid="blog-post-shop-link">Shop now</Link>
            </div>
          </div>
        )}
        {p.tags?.length > 0 && <ul className="mt-10 flex flex-wrap gap-2">{p.tags.map((t) => <li key={t} className="rounded-full border border-line px-3 py-1 text-xs text-gray-400">#{t}</li>)}</ul>}
      </article>
      {p.related?.length > 0 && (
        <section className="container-x pb-6" aria-labelledby="rel-title">
          <h2 id="rel-title" className="mb-6 text-3xl font-semibold text-white">Related articles</h2>
          <div className="grid gap-6 md:grid-cols-3">{p.related.map((r) => <BlogCard key={r.slug} p={r} />)}</div>
        </section>
      )}
      <CtaBand />
    </>
  );
}
